// OAuthCallback.tsx
// Provider (GitHub/Google/LinkedIn) authorize sayfasindan donen kullaniciyi karsilar.
// URL'den ?code=...&state=... parametrelerini alir, authService.completeOAuth ile
// backend'e gonderir. Basarili ise dashboard'a, hata varsa login'e yonlendirir.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Video, CheckCircle, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { authService } from '../services/auth.service.ts';

const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState<string>('');
  // StrictMode'da effect'in iki kere calismasini onle
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const err = searchParams.get('error');
    const errDescription = searchParams.get('error_description');

    // Provider tarafinda hata (kullanici izin vermedi, vs.)
    if (err) {
      setStatus('error');
      setErrorMessage(
        errDescription
          ? decodeURIComponent(errDescription)
          : `Yetkilendirme reddedildi (${err})`
      );
      return;
    }

    if (!code || !state) {
      setStatus('error');
      setErrorMessage(
        'Eksik parametre: yetkilendirme cevabı eksik. Lütfen tekrar deneyin.'
      );
      return;
    }

    (async () => {
      try {
        await authService.completeOAuth(code, state);
        setStatus('success');
        // 1 sn'lik kisa basari ekrani sonra dashboard
        setTimeout(() => navigate('/dashboard', { replace: true }), 800);
      } catch (e: any) {
        setStatus('error');
        setErrorMessage(
          e?.response?.data?.error ||
            e?.message ||
            'Bilinmeyen bir hata oluştu, lütfen tekrar deneyin.'
        );
      }
    })();
    // searchParams ve navigate stable; effect bir kez calismali
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-purple-500/30 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-blue-500/30 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur opacity-30"></div>
        <div className="relative bg-slate-800/80 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <Video className="w-8 h-8 text-purple-400" />
            <span className="text-2xl font-bold text-white">TBKMeet</span>
          </div>

          {status === 'pending' && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-purple-400 animate-spin" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-white">Giriş yapılıyor…</h2>
              <p className="text-gray-400 text-sm">
                Sosyal medya hesabınız doğrulanıyor. Lütfen bekleyin.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-white">Giriş başarılı!</h2>
              <p className="text-gray-400 text-sm">
                Yönlendiriliyorsunuz…
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-10 h-10 text-red-400" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-white">Giriş başarısız</h2>
              <div
                role="alert"
                className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm text-left"
              >
                {errorMessage}
              </div>

              <Link
                to="/login"
                className="inline-flex items-center justify-center space-x-2 w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold transition transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Giriş Sayfasına Dön</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
